import React, { useState, useEffect } from 'react';
import Component from '../../components/Component';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Alert,
  Snackbar,
  Pagination,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Edit, Delete, Add, CloudUpload, Search } from '@mui/icons-material';
import { createMeme, getMemes, updateMeme, deleteMeme } from '../../services/memesApi';

function Memes() {
  const [memes, setMemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingMeme, setEditingMeme] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    kind: 'image',
    category: '',
    context: '',
    emotion: '',
    keywords: [],
    image: null
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    keyword: '',
    limit: 20,
    offset: 0
  });
  
  const [totalPages, setTotalPages] = useState(0);
  const [newKeyword, setNewKeyword] = useState('');

  // Predefined options
  const kinds = ['image', 'gif', 'video'];
  const [categories, setCategories] = useState([
    'Anticipation', 'Argumentative', 'Catastrophic Blunder', 'Contrasting Reactions',
    'Critical Mistake', 'Devastating Loss', 'Dissonance', 'Domination', 'Extreme Frustration',
    'Extreme Joy', 'Foolishness', 'Futile Attempt', 'Heroic Moment', 'Interruption',
    'Momentum Shift', 'Overwhelming Odds', 'Pleasant Surprise', 'Rivalry/Hatred',
    'Roller Coaster', 'Stalemate', 'Watching Doom', 'Watching Individual Doom'
  ]);
  const [emotions, setEmotions] = useState([
    'Admiration', 'Amusement', 'Anger', 'Dominance', 'Helplessness', 'Hope',
    'Joy', 'Joy/Sadness', 'Negative Shock', 'Passion', 'Positive Shock', 'Rage',
    'Sadness', 'Shame', 'Stress', 'Uncertainty'
  ]);
  const [newCategory, setNewCategory] = useState('');
  const [newEmotion, setNewEmotion] = useState('');

  useEffect(() => {
    fetchMemes();
  }, [filters.offset, filters.limit]);

  const fetchMemes = async () => {
    setLoading(true);
    try {
      const response = await getMemes(filters);
      if (response.statusCode === "10000") {
        setMemes(response.data.memes || []);
        setTotalPages(Math.ceil((response.data.total || 0) / filters.limit));
      } else if (response.statusCode === "10003") {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error fetching memes', 'error');
        setMemes([]);
      }
    } catch (error) {
      showSnackbar('Error fetching memes', 'error');
      setMemes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (meme = null) => {
    if (meme) {
      setEditingMeme(meme);
      setFormData({
        name: meme.name || '',
        kind: meme.kind || 'image',
        category: meme.category || '',
        context: meme.context || '',
        emotion: meme.emotion || '',
        keywords: meme.keywords || [],
        image: null
      });
    } else {
      setEditingMeme(null);
      setFormData({
        name: '',
        kind: 'image',
        category: '',
        context: '',
        emotion: '',
        keywords: [],
        image: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMeme(null);
    setFormData({
      name: '',
      kind: 'image',
      category: '',
      context: '',
      emotion: '',
      keywords: [],
      image: null
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !formData.keywords.includes(newKeyword.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, newKeyword.trim()]
      }));
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keywordToRemove) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(keyword => keyword !== keywordToRemove)
    }));
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      const updatedCategories = [...categories, newCategory.trim()];
      setCategories(updatedCategories);
      setFormData(prev => ({
        ...prev,
        category: newCategory.trim()
      }));
      setNewCategory('');
    }
  };

  const handleAddEmotion = () => {
    if (newEmotion.trim() && !emotions.includes(newEmotion.trim())) {
      const updatedEmotions = [...emotions, newEmotion.trim()];
      setEmotions(updatedEmotions);
      setFormData(prev => ({
        ...prev,
        emotion: newEmotion.trim()
      }));
      setNewEmotion('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.kind || !formData.category) {
      showSnackbar('Name, Kind, and Category are required fields', 'error');
      return;
    }
    
    if (!formData.keywords || formData.keywords.length === 0) {
      showSnackbar('At least one keyword is required', 'error');
      return;
    }
    
    if (!editingMeme && !formData.image) {
      showSnackbar('Image is required for new memes', 'error');
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      if (editingMeme) {
        response = await updateMeme(editingMeme.id, formData);
      } else {
        response = await createMeme(formData);
      }
      
      if (response.statusCode === "10000") {
        showSnackbar(editingMeme ? 'Meme updated successfully' : 'Meme created successfully', 'success');
        handleCloseDialog();
        fetchMemes();
      } else if (response.statusCode === "10003") {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 2000);
      } else {
        showSnackbar(response.message || 'Error saving meme', 'error');
      }
    } catch (error) {
      showSnackbar('Error saving meme', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (memeId) => {
    if (window.confirm('Are you sure you want to delete this meme?')) {
      setLoading(true);
      try {
        const response = await deleteMeme(memeId);
        if (response.statusCode === "10000") {
          showSnackbar('Meme deleted successfully', 'success');
          fetchMemes();
        } else if (response.statusCode === "10003") {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 2000);
        } else {
          showSnackbar(response.message || 'Error deleting meme', 'error');
        }
      } catch (error) {
        showSnackbar('Error deleting meme', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearchChange = (e) => {
    setFilters(prev => ({
      ...prev,
      keyword: e.target.value
    }));
  };

  const handleSearch = () => {
    setFilters(prev => ({
      ...prev,
      offset: 0
    }));
    fetchMemes();
  };

  const handlePageChange = (event, page) => {
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * prev.limit
    }));
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <div>
      <Component>
        <div className="page-body">
          <div className="container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-6">
                  <h3>Memes Management</h3>
                </div>
                <div className="col-sm-6">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenDialog()}
                    sx={{ float: 'right' }}
                  >
                    Add New Meme
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="container-fluid">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    {/* Search Bar */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        name="keyword"
                        label="Search by keyword"
                        value={filters.keyword}
                        onChange={handleSearchChange}
                        placeholder="Enter keyword to search"
                        sx={{ flexGrow: 1, minWidth: 250 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={handleSearch} edge="end">
                                <Search />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      />
                      
                      <FormControl sx={{ minWidth: 100 }}>
                        <InputLabel>Limit</InputLabel>
                        <Select
                          name="limit"
                          value={filters.limit}
                          onChange={(e) => setFilters(prev => ({ ...prev, limit: e.target.value, offset: 0 }))}
                          label="Limit"
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={20}>20</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Memes Grid */}
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Grid container spacing={3}>
                        {memes.length === 0 ? (
                          <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                              <Typography variant="h6" color="text.secondary">
                                No memes found
                              </Typography>
                            </Box>
                          </Grid>
                        ) : (
                          memes.map((meme) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={meme.id}>
                              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardMedia
                                  component="img"
                                  height="250"
                                  image={meme.image_url || meme.url}
                                  alt={meme.name}
                                  sx={{ objectFit: 'cover', cursor: 'pointer' }}
                                  onClick={() => window.open(meme.image_url || meme.url, '_blank')}
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                  <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                                    {meme.name}
                                  </Typography>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>Category:</strong> {meme.category}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>Kind:</strong> {meme.kind}
                                    </Typography>
                                    {meme.emotion && (
                                      <Typography variant="body2" color="text.secondary">
                                        <strong>Emotion:</strong> {meme.emotion}
                                      </Typography>
                                    )}
                                  </Box>
                                  {meme.context && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                                      "{meme.context}"
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {meme.keywords && meme.keywords.map((keyword, index) => (
                                      <Chip
                                        key={index}
                                        label={keyword}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    ))}
                                  </Box>
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleOpenDialog(meme)}
                                    size="small"
                                  >
                                    <Edit />
                                  </IconButton>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDelete(meme.id)}
                                    size="small"
                                  >
                                    <Delete />
                                  </IconButton>
                                </CardActions>
                              </Card>
                            </Grid>
                          ))
                        )}
                      </Grid>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Pagination
                          count={totalPages}
                          page={Math.floor(filters.offset / filters.limit) + 1}
                          onChange={handlePageChange}
                          color="primary"
                        />
                      </Box>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingMeme ? 'Edit Meme' : 'Add New Meme'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Meme Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    helperText="Required field"
                  />
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Kind *</InputLabel>
                    <Select
                      name="kind"
                      value={formData.kind}
                      onChange={handleInputChange}
                      label="Kind *"
                      required
                    >
                      {kinds.map(kind => (
                        <MenuItem key={kind} value={kind}>{kind}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      label="Category *"
                      required
                    >
                      {categories.map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Add New Category"
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Button onClick={handleAddCategory} variant="outlined" size="small">
                      Add
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Emotion</InputLabel>
                    <Select
                      name="emotion"
                      value={formData.emotion}
                      onChange={handleInputChange}
                      label="Emotion"
                    >
                      <MenuItem value="">None</MenuItem>
                      {emotions.map(emo => (
                        <MenuItem key={emo} value={emo}>{emo}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="Add New Emotion"
                      value={newEmotion}
                      onChange={(e) => setNewEmotion(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmotion())}
                      size="small"
                      sx={{ flexGrow: 1 }}
                    />
                    <Button onClick={handleAddEmotion} variant="outlined" size="small">
                      Add
                    </Button>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="context"
                    label="Context / Description"
                    value={formData.context}
                    onChange={handleInputChange}
                    multiline
                    rows={2}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Keywords <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      label="Add Keyword"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                      size="small"
                      sx={{ flexGrow: 1 }}
                      placeholder="Enter keyword and click Add"
                    />
                    <Button onClick={handleAddKeyword} variant="outlined" size="small">
                      Add
                    </Button>
                  </Box>
                  {formData.keywords.length === 0 && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      Please add at least one keyword (required)
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {formData.keywords.map((keyword, index) => (
                      <Chip
                        key={index}
                        label={keyword}
                        onDelete={() => handleRemoveKeyword(keyword)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Meme Image <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    fullWidth
                    sx={{ py: 2 }}
                  >
                    {formData.image ? formData.image.name : editingMeme ? 'Upload New Image (optional)' : 'Upload Meme Image (required)'}
                    <input
                      type="file"
                      hidden
                      accept="image/*,.gif"
                      onChange={handleFileChange}
                      required={!editingMeme}
                    />
                  </Button>
                  {editingMeme && editingMeme.image_url && !formData.image && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Current Image:
                      </Typography>
                      <img 
                        src={editingMeme.image_url} 
                        alt="Current" 
                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={20} /> : (editingMeme ? 'Update' : 'Create')}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Component>
    </div>
  );
}

export default Memes;

